import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { animated, useSpring } from '@react-spring/web'
import { ArrowLeft } from 'lucide-react'
import {
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
	data,
	redirect,
	useFetcher,
	useLoaderData,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { AuthFormWrapper } from '#app/components/auth/AuthFormWrapper'
import { AuthHeader } from '#app/components/auth/AuthHeader'
import { AuthLayout } from '#app/components/auth/AuthLayout'
import { OtpEmail } from '#app/components/emails/OtpEmail'
import { GeneralErrorBoundary } from '#app/components/error-boundary'
import { ErrorList, FloatingLabelField } from '#app/components/forms'
import { AnimatedContainer } from '#app/components/ui/animated-container'
import { AnimatedStatusButton } from '#app/components/ui/animated-status-button'
import { StaggerChildren } from '#app/components/ui/stagger-children'
import { smoothSpring } from '#app/utils/animations'
import { getAuthConfig } from '#app/utils/auth-config.server'
import { db } from '#app/utils/db.server'
import { sendEmail } from '#app/utils/email.server'
import { checkHoneypot } from '#app/utils/honeypot.server'
import { getEmailBaseUrl } from '#app/utils/misc'
import { EmailSchema, UsernameSchema } from '#app/utils/user-validation'
import { verifySessionStorage } from '#app/utils/verification.server'
import { prepareVerification } from './verify.server'

const ForgotPasswordSchema = z.object({
	usernameOrEmail: z.union([EmailSchema, UsernameSchema]),
})

export async function loader(_request: LoaderFunctionArgs) {
	const authConfig = getAuthConfig('landlord')
	return { authConfig }
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		async: true,
		schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
			const userResult = await db.query.user.findFirst({
				columns: { id: true, userType: true },
				where: (user, { or, eq }) =>
					or(
						eq(user.email, data.usernameOrEmail),
						eq(user.username, data.usernameOrEmail),
					),
			})

			if (!userResult) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'No user exists with this username or email',
					path: ['usernameOrEmail'],
				})
				return
			}

			// Validate userType matches landlord
			if (userResult.userType !== 'landlord') {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						'This account is registered as a tenant. Please use the tenant forgot password page.',
					path: ['usernameOrEmail'],
				})
				return
			}
		}),
	})

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{
				status: submission.status === 'error' ? 400 : 200,
			},
		)
	}

	const { usernameOrEmail } = submission.value

	const userResult = await db.query.user.findFirst({
		columns: { email: true, username: true },
		where: (user, { or, eq }) =>
			or(eq(user.email, usernameOrEmail), eq(user.username, usernameOrEmail)),
	})

	if (!userResult) {
		throw new Error('User not found')
	}

	const {
		otp,
		redirectTo,
		verifyUrl: _verifyUrl,
	} = await prepareVerification({
		period: 10 * 60,
		request,
		target: usernameOrEmail,
		type: 'reset-password',
	})

	// Store userType in verification session
	const verifySession = await verifySessionStorage.getSession()
	verifySession.set('resetPasswordUserType', 'landlord')

	const response = await sendEmail({
		react: (
			<OtpEmail
				otp={otp}
				baseUrl={getEmailBaseUrl(request)}
				type="reset-password"
			/>
		),
		subject: 'TrueTenant Password Reset',
		to: userResult.email,
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString(), {
			headers: {
				'set-cookie': await verifySessionStorage.commitSession(verifySession),
			},
		})
	}
	return data(
		{
			result: submission.reply({ formErrors: [response.error.message] }),
		},
		{
			status: 500,
		},
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Password Recovery for TrueTenant - Landlord' }]
}

export default function LandlordForgotPasswordRoute() {
	const forgotPassword = useFetcher<typeof action>()
	const loaderData = useLoaderData<typeof loader>()
	const authConfig = loaderData.authConfig

	const [form, fields] = useForm({
		constraint: getZodConstraint(ForgotPasswordSchema),
		id: 'forgot-password-form',
		lastResult: forgotPassword.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	// React Spring animation for button interactions

	// React Spring animation for error messages
	const hasErrors = Boolean(form.errors?.length)
	const errorSpring = useSpring({
		opacity: hasErrors ? 1 : 0,
		y: hasErrors ? 0 : -10,
		config: smoothSpring,
	})

	const isPending = forgotPassword.state === 'submitting'

	return (
		<AuthLayout
			backgroundImage={authConfig.backgroundImage}
			heroText={authConfig.heroText}
			userType="landlord"
		>
			<AuthHeader />
			<AuthFormWrapper
				title={authConfig.titles.forgotPassword}
				subtitle={authConfig.subtitles.forgotPassword}
				titleClassName="text-[#32302F] text-[25px] md:text-[35px] capitalize font-medium"
				subtitleClassName="text-[#747474] font-400 text-[18px] md:text-[20px]"
				headerAction={
					<button
						type="button"
						onClick={() => history.back()}
						className="w-10 h-10 border-2 border-[#32302F] rounded-full hover:bg-black hover:text-white flex items-center justify-center transition"
					>
						<ArrowLeft size={18} />
					</button>
				}
			>
				<AnimatedContainer>
					<forgotPassword.Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<StaggerChildren>
							<FloatingLabelField
								inputProps={{
									...getInputProps(fields.usernameOrEmail, { type: 'text' }),
									autoFocus: true,
									autoComplete: 'username',
									placeholder: 'Enter Your Email Address',
									className:
										'placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] text-[#484848] border border-[#CCCCCC] rounded-[30px]',
								}}
								labelProps={{
									children: 'Email Address',
									htmlFor: fields.usernameOrEmail.id,
									className: 'text-[16px] text-[#32302F]',
								}}
								errorClass={'min-h-auto md:px-0 md:pt-0 md:pb-0 '}
								className="mb-[20px]"
								errors={fields.usernameOrEmail.errors}
							/>
							<animated.div style={errorSpring} className="mt-6 mb-2">
								<ErrorList errors={form.errors} id={form.errorId} />
							</animated.div>
							<AnimatedStatusButton
								className="w-full py-[23px] h-[60px] mt-[50px] mb-[30px] bg-[#32302F] text-[20px] text-[#FFFFFF] capitalize rounded-[30px] hover:bg-[#1a1918] hover:text-[#FFFFFF] active:bg-[#0f0f0e] transition-colors duration-200 border-0"
								status={isPending ? 'pending' : (form.status ?? 'idle')}
								type="submit"
								disabled={isPending}
								variant={'outline'}
							>
								Send Verification Code
							</AnimatedStatusButton>
						</StaggerChildren>
					</forgotPassword.Form>
				</AnimatedContainer>
			</AuthFormWrapper>
		</AuthLayout>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
